import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CreateDebtRequest, UpdateDebtRequest, Debt, Customer } from '../../types';
import DebtForm from './DebtForm';
import { PlanDebtInput } from './DebtForm';
import { apiClient } from '../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import MovementDialogLayout from './MovementDialogLayout';
import { debtErrorMessage } from '../../utils/debtErrorMessage';
import { useDebtMutations } from '../../hooks/useDebtMutations';

interface DebtDialogProps {
  open: boolean;
  onClose: () => void;
  debt?: Debt;
  onSuccess: () => void;
  prefilledCustomer?: Customer | null;
}

const DebtDialog: React.FC<DebtDialogProps> = ({
  open,
  onClose,
  debt,
  onSuccess,
  prefilledCustomer,
}) => {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const queryClient = useQueryClient();
  const { saveDebtMutation } = useDebtMutations();
  const isLoading = saveDebtMutation.isPending || planLoading;

  const isEditing = !!debt;
  const payment = (debt?.amount ?? 0) < 0;

  const handleSubmit = async (data: CreateDebtRequest | UpdateDebtRequest) => {
    try {
      setError(null);
      const input = data as PlanDebtInput;
      if (!isEditing && input.installmentPlan) {
        if (!input.customerId || input.amount === undefined) throw new Error('Customer and amount are required');
        setPlanLoading(true);
        await apiClient.paymentPlans.create({ customerId: input.customerId, description: input.description,
          totalAmount: input.amount, ...input.installmentPlan, transactionDateTime: input.transactionDateTime });
        await Promise.all([queryClient.invalidateQueries({ queryKey: ['debts'] }), queryClient.invalidateQueries({ queryKey: ['dashboard'] }), queryClient.invalidateQueries({ queryKey: ['payment-plans'] })]);
      } else {
        await saveDebtMutation.mutateAsync({ id: isEditing ? debt?.id : undefined, data });
      }

      onSuccess();
      onClose();
    } catch (error: unknown) {
      setError(debtErrorMessage(error, isEditing ? t('debts.updateError') : t('debts.createError'), t));
    } finally {
      setPlanLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  return (
    <MovementDialogLayout
      open={open}
      onClose={handleClose}
      title={isEditing ? payment ? t('debts.editPayment') : t('debts.editDebt') : t('debts.createDebt')}
      hint={payment ? t('debts.form.paymentHint') : t('debts.form.debtHint')}
      error={error}
      isLoading={isLoading}
    >
      <DebtForm
        debt={debt}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        isLoading={isLoading}
        isPayment={payment}
        prefilledCustomer={prefilledCustomer}
      />
    </MovementDialogLayout>
  );
};

export default DebtDialog;
